import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/hooks/useFormatters';
import { formatDoctorName } from '@/utils/doctor-name';
import type { Notification } from '@/types/notification.types';

type NotificationCopy = Pick<Notification, 'title' | 'message' | 'i18n'>;

/** Values whose meaning is a key in the catalogue, not text to show. */
const TRANSLATED_PARAMS: Record<string, string> = {
  reason: 'notifications.receiptReasons',
  status: 'notifications.ticketStatus',
  record: 'notifications.mentionRecords',
  method: 'notifications.paymentMethods',
};

/**
 * What a notification says, in the reader's language.
 *
 * The API stores an English `title` and `message` and, beside them, the
 * message as data (`i18n.key` + raw values). One row is read by everyone in
 * the clinic, so the sentence is written here, per reader. The stored text is
 * the fallback: rows from before the API sent `i18n`, and keys this build
 * does not know yet.
 *
 * Raw values are formatted by name, the convention the API follows:
 * `…At` is an instant, `…On` a calendar day, `…Month` a `YYYY-MM` month,
 * `…amount` money in `currency`.
 */
export function useNotificationText() {
  const { t, i18n } = useTranslation();
  const { date, dateTime, money } = useFormatters();

  return useCallback(
    (notification: NotificationCopy): { title: string; message: string } => {
      const stored = {
        title: notification.title,
        message: notification.message,
      };
      const key = notification.i18n?.key;
      if (!key) return stored;

      const raw = notification.i18n?.params ?? {};
      const currency = typeof raw.currency === 'string' ? raw.currency : null;
      const params: Record<string, string | number> = {};
      for (const [name, value] of Object.entries(raw)) {
        if (name === 'patient' || name === 'doctor' || name === 'by') {
          const person = typeof value === 'string' ? value.trim() : '';
          params[name] = !person
            ? t(`notifications.fallback.${name}`)
            : name === 'doctor'
              ? formatDoctorName(person, t)
              : person;
          continue;
        }
        if (value === null || value === undefined) continue;
        if (name in TRANSLATED_PARAMS) {
          const labelKey = `${TRANSLATED_PARAMS[name]}.${value}`;
          // An unknown reason or status cannot be worded: keep the API's text.
          if (!i18n.exists(labelKey)) return stored;
          params[name] = t(labelKey);
        } else if (/At$/.test(name)) {
          params[name] = dateTime(String(value));
        } else if (/On$/.test(name)) {
          params[name] = date(String(value));
        } else if (/Month$/.test(name) && /^\d{4}-\d{2}$/.test(String(value))) {
          params[name] = new Intl.DateTimeFormat(i18n.language, {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(`${value}-01T12:00:00Z`));
        } else if (/amount$/i.test(name) && currency) {
          params[name] = money(value, { currency });
        } else {
          params[name] = value;
        }
      }

      const titleKey = `notifications.messages.${key}.title`;
      const bodyKey = `notifications.messages.${key}.body`;
      return {
        title: i18n.exists(titleKey, params)
          ? t(titleKey, params)
          : stored.title,
        // A message with no body of its own (a chat preview) keeps the
        // stored one: it is the sender's words, not ours.
        message: i18n.exists(bodyKey, params)
          ? t(bodyKey, params)
          : stored.message,
      };
    },
    [t, i18n, date, dateTime, money],
  );
}
